/* -----------------Globals--------------- */
import { useCallback, useState } from "react";

/* -----------------Helpers & Hooks--------------- */
import { useReport } from "../hooks/useReport";

/* -----------------Types--------------- */
import { router, useLocalSearchParams } from "expo-router";

/* -----------------Child components--------------- */

import { cacheKeys } from "@constants/cache-keys";
import { events } from "@constants/tracking";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { SessionConfigDialog } from "@features/session-config/components/SessionConfigDialog";
import StudySessionProgressBar from "@features/session/components/StudySessionProgressBar";
import { flattenSessionItems } from "@features/session/helpers/session";
import { useSession } from "@features/session/hooks/useSession";
import useSessionStore from "@features/session/hooks/useSessionStore";
import { formatTimeString } from "@helpers/date";
import { isWeb } from "@helpers/platform";
import { useAppStore } from "@hooks/useAppStore";
import { useCustomNavigation } from "@hooks/useCustomNavigation";
import { useDevice } from "@hooks/useDevice";
import type { SessionItemTypes } from "@memorang/types/src/session/common";
import Box from "@memorang/ui/src/components/Box";
import Container from "@memorang/ui/src/components/Container";
import Loader from "@memorang/ui/src/components/Loader";
import StickyButtonContainer from "@memorang/ui/src/components/StickyButtonContainer";
import { useQuery } from "@tanstack/react-query";
import { trackCustomEvent } from "analytics";
import CustomAppbar from "components/CustomAppbar";
import isEmpty from "lodash-es/isEmpty";
import { useTranslation } from "react-i18next";
import { Appbar } from "react-native-paper";
import { ReportView } from "../components/ReportView";
import type {
	CheckboxStateMap,
	CheckboxType,
} from "../components/dialogs/ReviewAnswersDialog";
import { getFilteredSessionItems } from "../helpers/review";

const ReportContainer = () => {
	const [showReviewDialog, setShowReviewDialog] = useState(false);

	const { isMobile } = useDevice();

	const appConfigCards = useAppStore((state) => state.reportView?.cards);

	const shouldRenderItemReviewCard = appConfigCards?.find(
		(card) => card.type === "item-review",
	);

	const [showSessionConfigDialog, setShowSessionConfigDialog] = useState(false);

	const toggleSessionConfigDialog = () => {
		setShowSessionConfigDialog((prev) => !prev);
	};

	const { handleSessionConfigNavigation } = useCustomNavigation();
	const [checkboxStateMap, setCheckboxStateMap] = useState<CheckboxStateMap>({
		correct: false,
		incorrect: false,
		skipped: false,
		almostcorrect: false,
		marked: false,
	});
	const practiceTestLabel = useAppStore(
		(state) => state.exam.practiceTest?.label,
	);

	const handleChangeCheckboxStateMap = useCallback(
		(key: keyof CheckboxStateMap) => {
			setCheckboxStateMap((prev) => {
				return {
					...prev,
					[key]: !prev[key],
				};
			});
		},
		[],
	);
	const handleUpdateIncorrectCheckboxStateMap = useCallback(() => {
		setCheckboxStateMap((prev) => {
			return {
				...prev,
				incorrect: true,
			};
		});
	}, []);

	const { id: sessionNumericId } = useLocalSearchParams<{
		id: string;
	}>();

	const [fetchingReviewItems, setFetchingReviewItems] = useState(false);
	const resetSessionStore = useSessionStore((state) => state.resetSessionStore);
	const updateSession = useSessionStore((state) => state.updateSession);
	const updateCurrentBlockIndex = useSessionStore(
		(state) => state.updateCurrentBlockIndex,
	);

	const { resumeSession } = useSession();

	const {
		currentExamContext: { studyPackCollectionId },
	} = useExamContext();
	const isSummativeTestFromSessionStore = useSessionStore(
		(state) => state.isSummativeTest,
	);

	const { reportDetails } = useReport();
	const isSummativeTest = reportDetails
		? reportDetails?.reportSessionType === "SUMMATIVE" &&
			studyPackCollectionId !== 38
		: isSummativeTestFromSessionStore;
	const [selectedItemTypeForReview, setSelectedItemTypeForReview] = useState<
		Extract<SessionItemTypes, "QUESTION"> | undefined
	>();

	const { data: sessionItems } = useQuery({
		queryKey: [cacheKeys.sessionItems, sessionNumericId],
		queryFn: async () => {
			const response = await resumeSession({
				sessionId: Number(sessionNumericId),
			});
			const flattenedSessionItems = flattenSessionItems(response.sessionItems);
			return flattenedSessionItems.flat();
		},
		enabled:
			!!sessionNumericId &&
			!!shouldRenderItemReviewCard &&
			!isEmpty(reportDetails),
	});

	const handleReview = async (checkboxStateMap: CheckboxStateMap) => {
		if (!sessionNumericId) {
			return;
		}
		setFetchingReviewItems(true);
		const selectedKeys = Object.keys(checkboxStateMap).filter(
			(key) => checkboxStateMap[key as CheckboxType],
		) as CheckboxType[];
		const response = await resumeSession({
			sessionId: Number(sessionNumericId),
		});

		const flattenedSessionItems = flattenSessionItems(response.sessionItems);

		const filteredSessionItems = getFilteredSessionItems(
			flattenedSessionItems.flat(),
			selectedKeys,
			isSummativeTest,
		);

		updateCurrentBlockIndex(0);
		updateSession({
			sessionResponse: {
				...response,
				sessionItems: [filteredSessionItems],
			},
			isSummativeTest,
			inReviewMode: true,
		});

		setFetchingReviewItems(false);
		if (filteredSessionItems?.length) {
			router.push({
				pathname: "/(protected)/session/[id]/review",
				params: {
					id: sessionNumericId,
					index: "1",
				},
			});
		}
		setShowReviewDialog(false);
	};
	const handleContinue = () => {
		resetSessionStore();

		const url = isWeb ? "/home" : "/home?isRoutedFromSession=true";
		router.replace(url);
	};

	const handleClickActionChip = (type: string) => {
		// trackCustomEvent({
		//   eventName: events.suggestedChipClicked,
		//   type,
		// });
		switch (type) {
			case "review": {
				setSelectedItemTypeForReview("QUESTION");
				setShowReviewDialog(true);
				break;
			}
			case "study": {
				resetSessionStore();
				handleSessionConfigNavigation(toggleSessionConfigDialog);
				break;
			}
			default:
				break;
		}
	};

	const handleReviewButtonClick = () => {
		trackCustomEvent({
			eventName: events.reviewAnswersClicked,
			type: "QUESTION",
		});
		setSelectedItemTypeForReview("QUESTION");
		setShowReviewDialog(true);
	};

	const handleCloseReviewDialog = () => {
		setShowReviewDialog(false);
		setCheckboxStateMap({
			correct: false,
			incorrect: false,
			skipped: false,
			almostcorrect: false,
			marked: false,
		});
	};

	const breadcrumbs = isSummativeTest
		? [
				{
					label: practiceTestLabel ?? "Practice Tests",
					href: "/practice-tests",
					action: () => router.push("/practice-tests"),
				},
				{
					label: reportDetails?.examName!,
				},
				...(reportDetails
					? [
							{
								label: `Test #${reportDetails.numericId}`,
							},
						]
					: []),
			]
		: [];

	const creationTime = reportDetails
		? formatTimeString(reportDetails.sessionDate, !isMobile)
		: "";
	const caption = reportDetails
		? `Test #${reportDetails.numericId} ∙ ${creationTime}`
		: "";

	const handleTracking = useCallback(
		(eventName: string, params?: Record<string, string | number>) => {
			trackCustomEvent({
				eventName,
				...(params || {}),
				view: "session_report",
			});
		},
		[],
	);
	const { t } = useTranslation();

	return (
		<>
			{isSummativeTest && (
				<CustomAppbar
					back
					breadcrumbs={breadcrumbs}
					options={{
						headerTitle: reportDetails?.examName,
						subtitle: caption,
						headerRight: () => null,
					}}
					role="banner"
				/>
			)}
			<StickyButtonContainer
				buttonText={t("common.continue")}
				maxWidth="sm"
				disabled={!reportDetails}
				onPress={handleContinue}
				showButton={isMobile}
			>
				{/* biome-ignore lint/a11y/useSemanticElements: <explanation> */}
				<Box gap={16} flex={1} role="main">
					{!isSummativeTest && (
						<Appbar.Header
							style={{
								backgroundColor: "transparent",
							}}
						>
							<Container>
								<StudySessionProgressBar
									sessionType={reportDetails?.reportSessionType}
								/>
							</Container>
						</Appbar.Header>
					)}
					{reportDetails && Object.keys(reportDetails).length > 0 ? (
						<ReportView
							reportDetails={reportDetails}
							fetchingReviewItems={fetchingReviewItems}
							handleContinue={handleContinue}
							handleClickActionChip={handleClickActionChip}
							checkboxStateMap={checkboxStateMap}
							isSummativeTest={isSummativeTest}
							handleTracking={handleTracking}
							handleChangeCheckboxStateMap={handleChangeCheckboxStateMap}
							handleUpdateIncorrectCheckboxStateMap={
								handleUpdateIncorrectCheckboxStateMap
							}
							sessionItems={sessionItems}
							handleReviewButtonClick={handleReviewButtonClick}
							handleCloseReviewDialog={handleCloseReviewDialog}
							selectedItemTypeForReview={selectedItemTypeForReview}
							handleReview={handleReview}
							showReviewDialog={showReviewDialog}
							shouldRenderItemReviewCard={!!shouldRenderItemReviewCard}
						/>
					) : (
						<Container
							maxWidth="sm"
							containerStyle={{
								flexGrow: 1,
							}}
						>
							<Loader />
						</Container>
					)}
				</Box>
			</StickyButtonContainer>
			{showSessionConfigDialog ? (
				<SessionConfigDialog
					visible={showSessionConfigDialog}
					handleClose={toggleSessionConfigDialog}
				/>
			) : null}
		</>
	);
};

export default ReportContainer;
